import React, { useState } from 'react';
import {
    Button,
    Alert,
    Box,
    Typography
} from '@mui/material';
import BaseDialog from '../common/BaseDialog';
import { appointmentTypeShareService } from '../../services/appointmentTypeShareService';
import { extractApiErrorMessage } from '../../utils/apiUtils';

interface ShareAllDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const ShareAllDialog: React.FC<ShareAllDialogProps> = ({
    open,
    onClose,
    onSuccess
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async () => {
        try {
            setLoading(true);
            setError(null);
            await appointmentTypeShareService.shareAllWithBusinessUsers(true); // Always grant full write access
            onSuccess();
            handleClose();
        } catch (err: any) {
            console.error('Error sharing all appointment types:', err);
            setError(extractApiErrorMessage(err, 'Failed to share appointment types'));
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setError(null);
        onClose();
    };

    return (
        <BaseDialog
            open={open}
            onClose={handleClose}
            title="Share All Appointment Types"
            actions={
                <>
                    <Button onClick={handleClose} disabled={loading}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" disabled={loading}>
                        {loading ? 'Sharing...' : 'Share All'}
                    </Button>
                </>
            }
        >
            {error && <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>{error}</Alert>}

            <Box sx={{ py: 2 }}>
                <Typography variant="body1">
                    This will share all of your appointment types with all other users in your business.
                    They will be able to view, create, and modify appointments on your calendars.
                </Typography>
            </Box>
        </BaseDialog>
    );
};

export default ShareAllDialog;
